import { type FeatureOptionSubtype, type FeatureStandardDefs } from '@solinkcorp/event-standard';
import { type FeatureTabEntry } from '@solinkcorp/event-standard/lib/src/types';
import { useEffect, useMemo } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import useTabName from 'src/hooks/useTabName';
import { LocationStore } from 'stores/LocationStore';
import { getAvailableFeatures } from 'utils/FeatureStandardUtils';
import FeaturesUtil from 'utils/FeaturesUtil';
import {
  ALL_EVENTS_ROUTE,
  ALL_FEATURE,
  DELIVERY_EVENTS_ROUTE,
  DELIVERY_FEATURE,
  TAB_EVENTS,
} from 'vd-constants';
import useFluxStore from './useFluxStore';

export interface FeatureOptionType extends FeatureOptionSubtype {
  default?: boolean;
}
export interface FeatureOptionsType {
  [key: string]: FeatureOptionType;
}

export interface FeatureTagsType {
  histogram?: string;
  eventsQuery?: string;
}

export function useCurrentFeature(): string | undefined {
  const match = useMatch('/events/:feature/*');
  const navigate = useNavigate();
  const feature = match?.params?.feature;

  const tab = useTabName();
  const needsRedirect = !feature && tab === TAB_EVENTS;
  const isDeliveryOnly = FeaturesUtil.isDeliveryOnly();

  useEffect(() => {
    if (needsRedirect) {
      navigate(isDeliveryOnly ? DELIVERY_EVENTS_ROUTE : ALL_EVENTS_ROUTE);
    }
  }, [needsRedirect, isDeliveryOnly, navigate]);

  if (needsRedirect) {
    return isDeliveryOnly ? DELIVERY_FEATURE : ALL_FEATURE;
  }

  return feature;
}

/**
 * React hook which returns feature information.
 *
 * This will only ever return data for /events/:feature routes.
 *
 * Note that this will return `undefined` for most results if featureStandard
 * is undefined.
 */
export const useEventFeature = (featureStandard: FeatureStandardDefs | undefined) => {
  const feature = useCurrentFeature();

  const locations = useFluxStore(LocationStore, (store) => store.getLocations());

  const {
    featureOptions,
    featureFilter,
    featureTags,
    featureI18n,
  }: {
    featureOptions: FeatureOptionsType | undefined;
    featureFilter: FeatureTabEntry['filter'] | undefined;
    featureTags: NonNullable<FeatureTabEntry['forceInclude']>['tags'] | undefined;
    featureI18n: FeatureTabEntry['i18n'] | undefined;
  } = useMemo(() => {
    const isValidFeatureRoute = !!feature && !!featureStandard?.tabs[feature];

    if (feature && isValidFeatureRoute && featureStandard && locations?.length > 0) {
      const FeatureTabEntry = featureStandard.tabs[feature];

      const { eventSubtypes, filter, forceInclude, i18n } = FeatureTabEntry;

      const { availableFeatureSubtypes } = getAvailableFeatures(featureStandard, locations);
      const availableSubtypes = getAvailableFeatureSubtype(eventSubtypes, availableFeatureSubtypes);
      // Remove scheduled inspection from available subtypes until feature is imeplemented
      if (availableSubtypes['scheduled-inspection']) {
        delete availableSubtypes['scheduled-inspection'];
      }
      const eventSubtypeQuery = filter?.eventSubtypeQuery;

      const featureOptions: { [key: string]: FeatureOptionType } =
        Object.keys(availableSubtypes).length !== 1 && !!eventSubtypeQuery
          ? {
              [eventSubtypeQuery]: {
                name: 'All',
                selectable: true,
                enabled: true,
                default: true,
              },
              ...availableSubtypes,
            }
          : {
              ...availableSubtypes,
            };

      // ensure at least 1 feature option is selected as a default
      const featureOptionsList = Object.values(featureOptions);
      if (featureOptionsList.length > 0 && !featureOptionsList.some((opt) => opt.default)) {
        featureOptionsList[0].default = true;
      }

      return {
        featureOptions,
        featureFilter: filter,
        featureTags: forceInclude?.tags,
        featureI18n: i18n,
      };
    }

    return {
      featureOptions: undefined,
      featureFilter: undefined,
      featureTags: undefined,
      featureI18n: undefined,
    };
  }, [featureStandard, feature, locations]);

  return { feature, featureFilter, featureOptions, featureTags, featureI18n };
};

/**
 * Get the event subtypes from location features from CallHome
 */
function getAvailableFeatureSubtype(
  eventSubtypes: FeatureTabEntry['eventSubtypes'],
  availableFeatureSubtypes: Record<string, boolean>,
) {
  const availableSubtypes: {
    [key: string]: FeatureOptionSubtype;
  } = {};

  if (eventSubtypes) {
    Object.keys(eventSubtypes).forEach((subtype) => {
      if (availableFeatureSubtypes && availableFeatureSubtypes[subtype]) {
        availableSubtypes[subtype] = { ...eventSubtypes[subtype] };
      }
    });
  }

  return availableSubtypes;
}
